import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Trophy } from 'lucide-react';
import { plannerAPI } from '../../services/api';

const MilestoneTracker = () => {
  const [milestones, setMilestones] = useState([]);
  const [error, setError] = useState(null);

  const loadMilestones = useCallback(async () => {
    try {
      setError(null);
      const data = await plannerAPI.getMilestones();
      setMilestones(Array.isArray(data) ? data : []);
    } catch (loadError) {
      setError(loadError.message || 'Milestones unavailable');
    }
  }, []);

  useEffect(() => {
    loadMilestones();
  }, [loadMilestones]);

  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) {
        loadMilestones();
      }
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    const intervalId = setInterval(refresh, 60000);

    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      clearInterval(intervalId);
    };
  }, [loadMilestones]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-lg p-5 md:p-6"
    >
      <h2 className="text-xl font-bold text-white flex items-center mb-5">
        <Trophy className="w-6 h-6 text-amber-300 mr-3" />
        Milestones
      </h2>
      {error ? (
        <div className="text-sm text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">{error}</div>
      ) : milestones.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3].map((item) => <div key={item} className="h-24 rounded-lg bg-white/5 border border-white/10 animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-3">
          {milestones.slice(0, 4).map((milestone) => (
            <div key={milestone.id} className="bg-white/5 border border-white/10 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-white font-semibold">{milestone.title}</p>
                  <p className="text-gray-400 text-sm mt-1">{milestone.target}</p>
                </div>
                {milestone.completed ? <CheckCircle className="w-5 h-5 text-emerald-300 flex-shrink-0" /> : null}
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{milestone.source}</span>
                  <span>{Math.round(milestone.progress)}%</span>
                </div>
                <div className="h-2 bg-slate-800/80 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, Math.max(0, milestone.progress || 0))}%` }}
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.section>
  );
};

export default MilestoneTracker;
